'use client'

import { useState } from 'react'
import { HsCodeDto } from '@/modules/hs/types/hs-response'
import Link from '@/modules/shared/components/Link'
import { ChevronDown, ChevronUp } from 'lucide-react'
import HighlightedText from '@/modules/hs/components/HighlightedText'
import BookmarkButton from '@/modules/hs/components/common/BookmarkButton'

interface HsTreeNodeProps {
  code: HsCodeDto
  level: number
  highlightTerm?: string
}

export default function HsTreeNode({ code, level, highlightTerm }: HsTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Check if this is a detail code (8-10 digits, clickable). API might omit isDetail so we fallback to !code.children
  const isActualDetail = code.isDetail ?? !code.children
  const isDetailCode = isActualDetail && code.hsCode && code.hsCode.length >= 8

  // Render heading (4-6 digits) - non-clickable
  if (!isDetailCode) {
    const hasChildren = code.children && code.children.length > 0

    return (
      <div className="tree-level-1">
        <div className="heading-node">
          <div
            className={`heading-header flex items-center justify-between ${hasChildren ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={() => hasChildren && setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-2 flex-1">
              <span className="hs-code-new font-semibold text-sm">{code.hsCode}</span>
              <HighlightedText text={code.descriptionVN} searchTerm={highlightTerm} className="heading-title text-sm" />
            </div>
            {hasChildren && (
              <div className="flex items-center ml-4">
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </div>
            )}
          </div>

          {/* Render children */}
          {isExpanded && hasChildren && (
            <div className="tree-level-2">
              {code.children.map((child, idx) => (
                <HsTreeNode key={`${child.hsCode || idx}`} code={child} level={level + 1} highlightTerm={highlightTerm} />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Link
      href={`/chi-tiet-ma-hs/${code.hsCode}`}
      className="tariff-node"
    >
      <span className="tariff-code font-semibold text-sm">{code.hsCode}</span>
      <HighlightedText text={code.descriptionVN} searchTerm={highlightTerm} className="tariff-title" />
      <div className="tariff-badges">
        {/* Badges will be populated based on user data */}
      </div>
      <BookmarkButton hsId={code.hsCode} context="hs_search" stopPropagation />
    </Link>
  )
}
